import { AdditionalGame } from '@/components/AdditionalGame';
import Button from '@/components/Button';
import { GameInfoBlock } from '@/components/GameInfoBlock';
import { GameProps } from '@/components/Gameinterfaces';
import { Icon, IconVariant } from '@/components/Icon';
import { LogoVariant } from '@/components/LogoVariant';
import Modal, { ModalContent, ModalHeader } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import StickyBox from '@/components/StickyBox';
import { SummaryPrice } from '@/components/SummaryPrice';
import { WeeksNumberSelection } from '@/components/WeeksNumberSelection';
import { gluecksSpirale } from '@/config/game/gluecksSpirale';
import {
  ID_EUROJACKPOT,
  ID_KENO,
  ID_SPIEL77,
  ID_SUPER6,
} from '@/config/game/ids';
import { siegerchance } from '@/config/game/siegerchance';
import { spiel77 } from '@/config/game/spiel77';
import MarketingParams from '@/helpers/MarketingParams';
import { JackpotData, getNextJackpot } from '@/helpers/getNextJackpot';
import { isJackpotExpired } from '@/helpers/isJackpotExpired';
import { mapDrawDaysToRadioGroupItemProps } from '@/helpers/mapDrawDaysToRadioGroupItemProps';
import classnames from '@/utilities/classnames';
import pushDatalayer, { DataLayerEvent } from '@/utilities/pushDatalayer';
import replaceAbbreviations from '@/utilities/replaceAbbreviations';
import { findSubsidiaryByKey } from '@/utilities/subsidiaries';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

const PlayNumber = dynamic(() => import('./PlayNumber'));

interface GameStepDetailsProps extends GameProps {
  [key: string]: any;
  withOutNextButton?: boolean;
}

const getShortenedWeekday = (day: string | undefined) => {
  if (day === undefined) return '';
  return ' am ' + day?.slice(0, 2) + '.';
};

const GameStepDetails: React.FC<
  React.PropsWithChildren<GameStepDetailsProps>
> = (props) => {
  const [jackpotData, setJackpotData] = useState<JackpotData>();
  useEffect(() => {
    async function fetchData() {
      const data = { [spiel77.id]: await getNextJackpot(spiel77) };
      setJackpotData(data);
    }
    fetchData();
  }, []);

  const handleOnPlayNumberChange = (numbers) => {
    props.onPlayNumbersUpdate(props.id, numbers);
  };

  const handleOnDrawDaysChange: (day: string[]) => void = (day) => {
    pushDatalayer(
      `click_element_name`,
      `Ziehungen - ${day}`,
      DataLayerEvent.UserClick
    );

    props.onDrawDaysUpdate(props.id, day);
  };

  const handleOnWeeklySubscriptionChange = () => {
    pushDatalayer(
      `click_element_name`,
      `Anzahl der Wochen - Dauerspiel`,
      DataLayerEvent.UserClick
    );

    props.onWeeklySubscriptionUpdate(props.id);
  };

  const handleOnWeeksNumberChange = (weeksNumber) => {
    if (weeksNumber !== undefined) {
      pushDatalayer(
        `click_element_name`,
        `Anzahl der Wochen - ${weeksNumber}`,
        DataLayerEvent.UserClick
      );

      props.onWeeksNumberUpdate(props.id, weeksNumber);
    }
  };

  const handleOnDrawNumberChange = (drawNumbers) => {
    props.onDrawNumberUpdate(props.id, drawNumbers);
  };

  const handleOnDrawRateChange = (rate) => {
    props.onDrawRateUpdate(props.id, rate);
  };

  const handleOnAdditionalGameToggle = (id) => {
    pushDatalayer(
      `click_element_name`,
      `Zusatzlotterien - ${id}`,
      DataLayerEvent.UserClick
    );

    props.onAdditionalGameToggle(props.id, +id);
  };

  const formIsValid =
    typeof props.drawDays === 'undefined' || props.selectedDrawDays.length > 0;

  const hasSuperNumber: boolean =
    !!props.playNumber && props.playNumber.superNumberIndex !== -1;

  const playNumberModalInfo = props.info && props.info.playNumber;
  const temporaryChangesModalInfo = props.info && props.info.temporaryChanges;
  const onlySaturdayModalInfo = props.info && props.info.onlySaturday;
  const changes2022_1 = props.info && props.info.changes2022_1;
  const changes2022_2 = props.info && props.info.changes2022_2;

  const externalGluecksSpirale =
    props.id === gluecksSpirale.id && props.external;
  const externalEurojackpot = props.id === ID_EUROJACKPOT && props.external;

  const iframeGluecksSpirale = props.id === gluecksSpirale.id && props.iframe;
  const searchParams = useSearchParams();
  const search = searchParams.get('s');
  const [pageTitle, setPageTitle] = useState(`${props.title} spielen`);
  useEffect(() => {
    async function searchForSubsidiary(search: string) {
      const sub = findSubsidiaryByKey(search);
      if (sub) {
        setPageTitle(`${props.title} in ${sub.displayName} spielen`);
        localStorage?.setItem('currentSubsidiary', sub.id);
        (window as any).history.pushState(
          {},
          document.title,
          window.location.pathname
        );
      }
    }
    // URL-Parameter bei GlücksSpirale IFrame?
    if (iframeGluecksSpirale && search) {
      searchForSubsidiary(search);
    }
  }, [search, iframeGluecksSpirale, props.title]);

  return (
    <>
      {externalGluecksSpirale && (
        <div className="h-flex__col h-flex__col--10-10 h-spacer-larger-bottom gs-info">
          <Icon variant={IconVariant.Info} />
          <p>
            In diesem Schritt sind nur die bundesweit verfügbaren Spielformen
            auswählbar. Weitere Optionen wie zusätzliche Losarten, die
            Sieger-Chance, das Jahreslos und längere Laufzeiten sind im nächsten
            Schritt – je nach Bundesland – verfügbar.
          </p>
        </div>
      )}
      <form
        onSubmit={($event) => {
          $event.preventDefault();
          props.handleNextStep();
        }}
        className="h-flex-gm h-flex-gm--cols"
      >
        <div className="h-flex__col h-flex__col--6-10 h-spacer-larger-bottom">
          {props.playNumber &&
            props.selectedPlayNumber &&
            props.additionalGames && (
              <div className="Game__groupContainer">
                <h1 className="Step__title">{pageTitle}</h1>
                <div className="gs-playnumber h-width-100">
                  <h2 className="Step__header">
                    <span>Losnummer eingeben</span>
                    {playNumberModalInfo && (
                      <>
                        <Button
                          ariaLabel="Weitere Informationen"
                          onClick={() => Modal.open('coupon-info')}
                        >
                          <Icon variant={IconVariant.Info} />
                        </Button>

                        <Modal id="coupon-info">
                          <ModalHeader>
                            <span>Schließen</span>
                          </ModalHeader>
                          <ModalContent>
                            <h4>{playNumberModalInfo.title}</h4>
                            <p
                              dangerouslySetInnerHTML={replaceAbbreviations(
                                playNumberModalInfo.desc
                              )}
                            />
                          </ModalContent>
                        </Modal>
                      </>
                    )}
                  </h2>

                  <PlayNumber
                    {...props.playNumber}
                    onChange={handleOnPlayNumberChange}
                    selectedNumbers={props.selectedPlayNumber}
                    labels={[
                      {
                        title: 'Superzahl',
                        isActive: hasSuperNumber,
                        isDisabled: !hasSuperNumber,
                        digitAmount: 1,
                      },
                      ...props.additionalGames.map((additionalGame) => {
                        const {
                          id: additionalGameConfigId,
                          label,
                          logoCompact,
                          logoColor,
                          additionalGameId,
                        } = additionalGame;
                        const resolvedPlayNumberLogo =
                          externalEurojackpot &&
                          additionalGameConfigId === ID_SPIEL77
                            ? LogoVariant.Spiel77OnBackground
                            : externalEurojackpot &&
                                additionalGameConfigId === ID_SUPER6
                              ? LogoVariant.Super6OnBackground
                              : logoCompact || logoColor;
                        return {
                          title: label!.text,
                          ...(((props.id === gluecksSpirale.id &&
                            props.external != true) ||
                            props.id !== gluecksSpirale.id) && {
                            logoColor: resolvedPlayNumberLogo,
                          }),
                          isActive:
                            props.selectedAdditionalGames &&
                            props.selectedAdditionalGames.some(
                              (id) => id === additionalGameId
                            ),
                          digitAmount: additionalGame.digitAmount!,
                        };
                      }),
                    ]}
                  />
                </div>
              </div>
            )}

          {externalGluecksSpirale && (
            <div className="Game__groupContainer gs-lot-type">
              <h3 className="Step__header">Losart</h3>

              <div className="gs-lot-type-lot">
                <span className="gs-lot-type-lot__radio checked"></span>
                <span className="gs-lot-type-lot__text checked">
                  5 Euro Los
                </span>
              </div>
              <div className="gs-lot-type-wrapper">
                <div className="gs-lot-type-lot">
                  <span className="gs-lot-type-lot__radio"></span>
                  <span className="gs-lot-type-lot__text">2,50 Euro Los</span>
                </div>
                <div className="gs-lot-type-lot">
                  <span className="gs-lot-type-lot__radio"></span>
                  <span className="gs-lot-type-lot__text">1 Euro Los</span>
                </div>
              </div>
            </div>
          )}

          {props.additionalGames && (
            <div className="Game__groupContainer gs-additional-games">
              <h3 className="Step__header">Zusatzlotterien</h3>
              {props.additionalGames.map((additionalGame) => {
                const {
                  id,
                  logoColor,
                  logoCompact,
                  title,
                  color,
                  data,
                  chanceDesc,
                  jackpotDesc,
                  jackpotLabelPrefix,
                  jackpotLabelSuffix,
                  jackpotOfflineDescGame,
                } = additionalGame;

                const jackpotDataItem = jackpotData && jackpotData[id];

                let jackpotPrefix = jackpotLabelPrefix;
                let jackpotLabel =
                  jackpotDataItem && jackpotDataItem.label
                    ? isJackpotExpired(jackpotDataItem)
                      ? jackpotOfflineDescGame
                      : jackpotDataItem.label
                    : jackpotDesc;

                if (
                  props.id === gluecksSpirale.id &&
                  additionalGame.id === spiel77.id &&
                  jackpotDataItem?.day === 'Mittwoch'
                ) {
                  // https://jira.tln-hannover.de/browse/LOTREL-614
                  // spiel77 wednesday jackpot should not be shown on gluecksspirale
                  // because spiel77 with gluecksspirale can only be played on saturdays
                  jackpotLabel = 'mind. 177.777 €';
                } else if (additionalGame.id === spiel77.id) {
                  jackpotPrefix += getShortenedWeekday(jackpotDataItem?.day);
                }

                const resolvedLogo =
                  externalEurojackpot && id === ID_SPIEL77
                    ? LogoVariant.Spiel77OnBackground
                    : externalEurojackpot && id === ID_SUPER6
                      ? LogoVariant.Super6OnBackground
                      : logoCompact || logoColor;

                return (
                  <AdditionalGame
                    key={additionalGame.additionalGameId}
                    additionalGameId={additionalGame.additionalGameId}
                    logoColor={resolvedLogo}
                    title={title}
                    color={color}
                    price={data && data.price}
                    chanceDesc={chanceDesc}
                    checked={
                      props.selectedAdditionalGames &&
                      props.selectedAdditionalGames.some(
                        (_id) => _id === additionalGame.additionalGameId
                      )
                    }
                    jackpotLabel={jackpotLabel}
                    jackpotLabelPrefix={
                      jackpotDataItem
                        ? isJackpotExpired(jackpotDataItem)
                          ? jackpotLabelPrefix
                            ? jackpotLabelPrefix
                            : ''
                          : jackpotPrefix
                        : jackpotPrefix
                    }
                    jackpotLabelSuffix={
                      jackpotDataItem
                        ? isJackpotExpired(jackpotDataItem)
                          ? ''
                          : jackpotLabelSuffix
                        : jackpotLabelSuffix
                    }
                    onChange={handleOnAdditionalGameToggle}
                  />
                );
              })}

              {externalGluecksSpirale && (
                <AdditionalGame
                  key={siegerchance.id}
                  additionalGameId={siegerchance.additionalGameId}
                  title={siegerchance.title}
                  chanceDesc={siegerchance.chanceDesc}
                  color={'color:red'}
                  disabled={true}
                  checked={false}
                  price={3}
                  logoColor={siegerchance.logo}
                  jackpotLabel={siegerchance.jackpotDesc}
                  onChange={() => {}}
                />
              )}
              {externalGluecksSpirale && <span></span>}
            </div>
          )}

          {props.drawDays &&
            props.drawDays.options &&
            props.drawDays.options.length >= 1 && (
              <div className="Game__groupContainer">
                <h3 className="Step__header">
                  <span>Ziehungen</span>
                  {onlySaturdayModalInfo && onlySaturdayModalInfo.active && (
                    <>
                      <Button onClick={() => Modal.open('onlySaturday-info')}>
                        <Icon variant={IconVariant.Info} />
                      </Button>

                      <Modal id="onlySaturday-info">
                        <ModalHeader>
                          <span>Schließen</span>
                        </ModalHeader>
                        <ModalContent>
                          <h4>{onlySaturdayModalInfo.title}</h4>
                          <p>
                            Ab dem 23.09.2020 tritt ein{' '}
                            <a
                              target="_blank"
                              href="https://www.lotto.de/lotto-6aus49/info/gewinnplanaenderung"
                              rel="noopener noreferrer"
                            >
                              neuer Gewinnplan für LOTTO 6aus49
                            </a>{' '}
                            in Kraft, daher steht die Auswahl der Ziehungstage
                            erst wieder ab dem 19.09.2020 um 19 Uhr in vollem
                            Umfang zur Verfügung.
                          </p>
                        </ModalContent>
                      </Modal>
                    </>
                  )}
                  {changes2022_1 && changes2022_1.active && (
                    <>
                      <Button onClick={() => Modal.open('changes2022_1-info')}>
                        <Icon variant={IconVariant.Info} />
                      </Button>

                      <Modal id="changes2022_1-info">
                        <ModalHeader>
                          <span>Schließen</span>
                        </ModalHeader>
                        <ModalContent>
                          <h4>{changes2022_1.title}</h4>
                          <p>
                            Im Rahmen der Eurojackpot-Produktänderung am
                            25.03.2022 ist der zweite Ziehungstag „Dienstag“ ab
                            dem 22.03.2022 um 19:00 Uhr auswählbar. Für
                            Anpassungen der Online-Spielsysteme der
                            Landeslotteriegesellschaften sind außerdem die
                            Zusatzlotterien vom 04.02.2022 ab 19:00 Uhr bis zum
                            18.03.2022 um 19:00 Uhr{' '}
                            <span
                              style={{
                                textDecoration: 'underline',
                              }}
                            >
                              nicht
                            </span>{' '}
                            verfügbar.
                          </p>
                        </ModalContent>
                      </Modal>
                    </>
                  )}
                </h3>
                <RadioGroup
                  id="draw-days"
                  title="Ziehungen"
                  onChange={handleOnDrawDaysChange}
                  currentChecked={props.selectedDrawDays}
                  radios={mapDrawDaysToRadioGroupItemProps(
                    props.drawDays.options
                  )}
                  shortName={true}
                />
              </div>
            )}
          {props.weeksNumber && (
            <div className="Game__groupContainer gs-runtime">
              <h3 className="Step__header">
                <span>Anzahl der Wochen</span>
                {temporaryChangesModalInfo &&
                  temporaryChangesModalInfo.active && (
                    <>
                      <Button
                        onClick={() => Modal.open('temporaryChanges-info')}
                      >
                        <Icon variant={IconVariant.Info} />
                      </Button>

                      <Modal id="temporaryChanges-info">
                        <ModalHeader>
                          <span>Schließen</span>
                        </ModalHeader>
                        <ModalContent>
                          <h4>{temporaryChangesModalInfo.title}</h4>
                          <p>
                            Ab dem 23.09.2020 tritt ein{' '}
                            <a
                              target="_blank"
                              href="https://www.lotto.de/lotto-6aus49/info/gewinnplanaenderung"
                              rel="noopener noreferrer"
                            >
                              neuer Gewinnplan für LOTTO 6aus49
                            </a>{' '}
                            in Kraft, daher stehen das Dauerspiel und die
                            Mehrwochen-Auswahl erst wieder ab dem 19.09.2020 um
                            19 Uhr in vollem Umfang zur Verfügung.
                          </p>
                        </ModalContent>
                      </Modal>
                    </>
                  )}
                {changes2022_2 && changes2022_2.active && (
                  <>
                    <Button onClick={() => Modal.open('changes2022_2-info')}>
                      <Icon variant={IconVariant.Info} />
                    </Button>

                    <Modal id="changes2022_2-info">
                      <ModalHeader>
                        <span>Schließen</span>
                      </ModalHeader>
                      <ModalContent>
                        <h4>{changes2022_2.title}</h4>
                        <p>
                          Für Anpassungen der Online-Spielsysteme der
                          Landeslotteriegesellschaften im Rahmen der
                          Eurojackpot-Produktänderung wird die auswählbare
                          Wochenlaufzeit ab dem 11.02.2022 ab 19:00 Uhr
                          sukzessiv reduziert. Ab dem 18.03.2022 um 19:00 Uhr
                          ist wieder die max. Anzahl von 5 Wochen auswählbar.
                        </p>
                      </ModalContent>
                    </Modal>
                  </>
                )}
              </h3>

              <WeeksNumberSelection
                options={props.weeksNumber}
                selectedValue={props.selectedWeeksNumber}
                onChange={handleOnWeeksNumberChange}
                hasWeeklySubscriptionOption={!!props.weeklySubscription}
                selectedWeeklySubscription={props.selectedWeeklySubscription}
                onWeeklySubscriptionChange={handleOnWeeklySubscriptionChange}
              />
              {externalGluecksSpirale && (
                <div className="gs-runtime-box">
                  <span className="gs-runtime-box__checkbox"></span>
                  <span className="gs-runtime-box__text">Jahreslos</span>
                </div>
              )}

              <div aria-live="polite">
                {props.selectedWeeklySubscription && (
                  <GameInfoBlock title="Spieleinsatz beim Dauerspiel">
                    Die Angebote für ein Dauerspiel können in den einzelnen
                    Bundesländern voneinander abweichen. Aus diesem Grund können
                    wir Ihren Spieleinsatz auf LOTTO.de leider noch nicht
                    ausweisen. Wenn Sie einen Spielschein auf LOTTO.de als
                    Dauerspiel abgeben, werden Sie an die jeweilige
                    Landeslotteriegesellschaft Ihres Bundeslandes weitergeleitet
                    – dort erhalten Sie weitere Informationen zu Spieleinsatz
                    und Gebühren.
                  </GameInfoBlock>
                )}
              </div>
            </div>
          )}

          {props.drawRate && (
            <div className="Game__groupContainer">
              <h3 className="Step__header">Einsatz pro Spielfeld</h3>
              <WeeksNumberSelection
                options={props.drawRate}
                label="Einsatz pro Spielfeld in Euro"
                selectedValue={props.selectedDrawRate}
                onChange={handleOnDrawRateChange}
              />
            </div>
          )}

          {props.drawNumbers && (
            <div className="Game__groupContainer">
              <h3 className="Step__header">Anzahl der Ziehungen</h3>
              <WeeksNumberSelection
                options={props.drawNumbers}
                label="Anzahl der Ziehungen"
                selectedValue={props.selectedDrawNumber}
                onChange={handleOnDrawNumberChange}
                suffixes={['Ziehung', 'Ziehungen']}
              />
            </div>
          )}
        </div>
        {!props.withOutNextButton && (
          <div className="h-flex__col h-flex__col--4-10 ">
            <StickyBox>
              <p className="h-spacer-medium-bottom">
                <Button
                  type="submit"
                  variant={Button.Variant.Primary}
                  iconPosition={Button.IconPosition.Right}
                  fullWidth={true}
                  progress={
                    formIsValid
                      ? props.selectedPlayNumber.length ===
                        (props.id === ID_KENO ? 5 : 7)
                        ? 100
                        : 0
                      : 0
                  }
                  onClick={() => {
                    pushDatalayer(
                      `click_element_name`,
                      `Button - Weiter (Zusatzlotterien)`,
                      DataLayerEvent.UserClick
                    );
                  }}
                >
                  <span>Weiter</span>
                  <Icon variant={IconVariant.ArrowRight} />
                </Button>
              </p>
              <SummaryPrice
                title={
                  !externalGluecksSpirale ? 'Ihr Spieleinsatz:' : 'Spieleinsatz'
                }
                subtitle="zzgl. Bearbeitungsgebühr"
                price={props.gamePrice}
              />

              {MarketingParams.isCashback() && (
                <div>
                  <div
                    className={classnames(
                      'Cashback--tile',
                      `Cashback--tile-${MarketingParams.getNetwork()}`
                    )}
                  >
                    <div>
                      <div>
                        <img
                          src={MarketingParams.getNetworkLogo('game-module')}
                          alt=""
                        />
                        <div className={classnames('Cashback--tile-text')}>
                          Beim Kauf sammeln Sie{' '}
                          <b>{MarketingParams.getRewardCurrency()}</b>. Die
                          Abgabe des Spielauftrags erfolgt über Ihre zuständige
                          Landeslotteriegesellschaft.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </StickyBox>
          </div>
        )}
      </form>
    </>
  );
};

export default GameStepDetails;
